/* eslint-disable */
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { Loader2, Layers } from 'lucide-react';
import { getHigherUnitAndQuantity } from '@/Helpers';
import inventoryAnalyticsServices from '@/Services/Analytics Services/inventory-analytics.services';
import { modalActions } from '@/Actions';

// Modal body: fetches batch-wise dispatch info for the given poItemIds and
// renders a simple table of Batch Name + Dispatched Qty (converted to higher unit).
const BatchDispatchInfoTable = ({ poItemIds = [], shipmentItemIds = [], unit = '' }) => {
	const [loading, setLoading] = useState(true);
	const [rows, setRows] = useState([]);

	// Shipment context sends shipmentItemIds, PO/buyer context sends poItemIds.
	const requestData = shipmentItemIds?.length
		? { shipmentItemIds }
		: { poItemIds };

	const fmtQty = (value) => {
		if (value == null) return '-';
		const [convertedValue, convertedUnit] = getHigherUnitAndQuantity(unit, value);
		return `${convertedValue} ${convertedUnit}`;
	};

	const fmtDate = (value) => (value ? moment(value).format('DD-MM-YYYY') : '-');

	useEffect(() => {
		let active = true;
		const run = async () => {
			setLoading(true);
			try {
				const response = await inventoryAnalyticsServices.batchwiseDispatchInfo({
					data: requestData,
				});
				const data = response?.statusCode === 200 ? (response?.data ?? []) : [];
				if (active) setRows(Array.isArray(data) ? data : []);
			} catch (error) {
				// eslint-disable-next-line no-console
				console.log('error', error);
				if (active) setRows([]);
			} finally {
				if (active) setLoading(false);
			}
		};
		run();
		return () => {
			active = false;
		};
	}, [JSON.stringify(poItemIds), JSON.stringify(shipmentItemIds)]);

	if (loading) {
		return (
			<div className='flex items-center justify-center gap-2 py-10 text-sm text-gray-500'>
				<Loader2 className='w-4 h-4 animate-spin' />
				Loading batch details...
			</div>
		);
	}

	if (!rows?.length) {
		return (
			<div className='py-10 text-center'>
				<p className='text-sm font-medium text-gray-500'>No batch dispatch info found</p>
				<p className='mt-1 text-xs text-gray-400'>
					This item has not been dispatched from any batch yet.
				</p>
			</div>
		);
	}

	return (
		<div className='max-h-[55vh] overflow-auto border border-gray-200 rounded-lg'>
			<table className='w-full text-xs text-left'>
				<thead>
					<tr className='sticky top-0 bg-gray-50 text-[10px] font-semibold tracking-wide text-gray-500 uppercase'>
						<th className='px-3 py-2 text-left'>Batch Name</th>
						<th className='px-3 py-2 text-left'>Mfg Date</th>
						<th className='px-3 py-2 text-left'>Expiry Date</th>
						<th className='px-3 py-2 text-left'>Dispatched Qty</th>
					</tr>
				</thead>
				<tbody className='divide-y divide-gray-100'>
					{rows.map((row, idx) => (
						<tr key={row?.id ?? row?.batchId ?? idx} className='hover:bg-gray-50'>
							<td className='px-3 py-2 font-semibold text-left text-gray-800'>
								{row?.batchName ?? '-'}
							</td>
							<td className='px-3 py-2 text-left text-gray-600'>
								{fmtDate(row?.manufacturedDate ?? row?.mfgDate)}
							</td>
							<td className='px-3 py-2 text-left text-gray-600'>
								{fmtDate(row?.expiryDate ?? row?.expiredDate)}
							</td>
							<td className='px-3 py-2 font-semibold text-left text-amber-600'>
								{fmtQty(row?.qty)}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

// Trigger: a small pill link rendered next to a product name. On click it opens
// the global CustomModal with the batch dispatch info table.
const BatchDispatchInfo = ({ poItemIds = [], shipmentItemIds = [], unit = '', productName = '', label = 'View Batch Detail' }) => {
	const dispatch = useDispatch();

	if (!poItemIds?.length && !shipmentItemIds?.length) return null;

	const openModal = () => {
		dispatch(
			modalActions.toggleCustomModal(true, {
				header: `Batch Details${productName ? ` — ${productName}` : ''}`,
				subtitle: 'Batch-wise dispatched quantities for this item',
				width: '44rem',
				height: 'auto',
				hideFooter: true,
				bodyContent: (
					<BatchDispatchInfoTable
						poItemIds={poItemIds}
						shipmentItemIds={shipmentItemIds}
						unit={unit}
					/>
				),
			})
		);
	};

	return (
		<button
			type='button'
			onClick={openModal}
			className='mt-1 inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-600 transition-colors hover:bg-blue-100 hover:text-blue-700'
		>
			<Layers size={12} />
			{label}
		</button>
	);
};

export { BatchDispatchInfoTable };
export default BatchDispatchInfo;
